import { useState, MutableRefObject } from "react";
import {
  Control,
  FieldErrors,
  UseFormRegister,
  UseFormSetValue,
  useFieldArray,
  useWatch,
} from "react-hook-form";
import { Button, ButtonAdd } from "@/components/ui/button";
import LabelHint from "@/components/ui/LabelHint";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2 } from "lucide-react";
import { useGetSuppliersQuery } from "@/redux/services/inventory/supplier.api";
import { useGetIngredientsQuery } from "@/redux/services/inventory/ingredient.api";
import {
  Ingredient,
  Supplier,
} from "@/redux/services/inventory/inventory.type";
import {
  BulkPurchaseOrderFormData,
  emptyOrderLine,
  bulkOrderTotalCents,
} from "@/hooks/inventory/useBulkPurchaseOrders";
import {
  displayPrice,
  normalizeDecimalInput,
  parseMoneyToCents,
} from "@/lib/utils";
import { useRestaurantCurrency } from "@/lib/hooks";
import { useDebounce } from "@/hooks/useDebounce";
import AsyncCombobox from "@/components/inventory/AsyncCombobox";

const PACKAGING_BASE_SENTINEL = "__base__";

// Users type amounts with spaces as thousand separators (e.g. "1 000 000").
// Strip all whitespace so it never reaches validation, the totals, or the API.
const stripSpaces = (v: unknown) =>
  typeof v === "string" ? v.replace(/\s/g, "") : v;

interface BulkSupplierOrderBlockProps {
  index: number;
  restaurantId: string;
  control: Control<BulkPurchaseOrderFormData>;
  register: UseFormRegister<BulkPurchaseOrderFormData>;
  errors: FieldErrors<BulkPurchaseOrderFormData>;
  setValue: UseFormSetValue<BulkPurchaseOrderFormData>;
  supplierCache: MutableRefObject<Map<string, Supplier>>;
  ingredientCache: MutableRefObject<Map<string, Ingredient>>;
  onRemove: () => void;
  canRemove: boolean;
  onCreateSupplier: () => void;
}

const BulkSupplierOrderBlock = ({
  index,
  restaurantId,
  control,
  register,
  errors,
  setValue,
  supplierCache,
  ingredientCache,
  onRemove,
  canRemove,
  onCreateSupplier,
}: BulkSupplierOrderBlockProps) => {
  const currency = useRestaurantCurrency();

  // Supplier search
  const [supplierSearch, setSupplierSearch] = useState("");
  const debouncedSupplierSearch = useDebounce(supplierSearch, 300);
  const { data: suppliersData, isFetching: isFetchingSuppliers } =
    useGetSuppliersQuery({
      restaurantId,
      query: debouncedSupplierSearch.trim()
        ? { search: debouncedSupplierSearch.trim() }
        : {},
    });

  // Ingredient search
  const [ingredientSearch, setIngredientSearch] = useState("");
  const debouncedIngredientSearch = useDebounce(ingredientSearch, 300);
  const { data: ingredientsData, isFetching: isFetchingIngredients } =
    useGetIngredientsQuery({
      restaurantId,
      query: debouncedIngredientSearch.trim()
        ? { name: debouncedIngredientSearch.trim() }
        : {},
    });

  const suppliers = suppliersData?.results ?? [];
  const ingredients = ingredientsData?.results ?? [];
  suppliers.forEach((s) => supplierCache.current.set(s.id, s));
  ingredients.forEach((i) => ingredientCache.current.set(i.id, i));

  const { fields, append, remove } = useFieldArray({
    control,
    name: `orders.${index}.lines`,
  });

  const order = useWatch({ control, name: `orders.${index}` });
  const watchedLines = order?.lines ?? [];
  const orderErrors = errors.orders?.[index];

  const subtotal = bulkOrderTotalCents(order);

  return (
    <div className="dash__card space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-card-foreground">
          Fournisseur {index + 1}
        </h3>
        {canRemove && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onRemove}
            className="text-muted-foreground hover:text-danger"
          >
            <Trash2 className="h-4 w-4 mr-1" />
            Retirer
          </Button>
        )}
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-card-foreground mb-1">
            Fournisseur <span className="text-danger">*</span>
          </label>
          <div className="flex items-start gap-2">
            <div className="flex-1 min-w-0">
              <AsyncCombobox
                value={order?.supplier ?? ""}
                onChange={(v) =>
                  setValue(`orders.${index}.supplier`, v, {
                    shouldValidate: true,
                  })
                }
                options={suppliers.map((s) => ({
                  value: s.id,
                  label: s.name,
                }))}
                selectedLabel={
                  supplierCache.current.get(order?.supplier ?? "")?.name
                }
                placeholder="Sélectionner un fournisseur"
                searchPlaceholder="Rechercher par nom, contact, téléphone..."
                emptyMessage="Aucun fournisseur"
                isLoading={isFetchingSuppliers}
                onSearchChange={setSupplierSearch}
              />
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onCreateSupplier}
              className="shrink-0 h-[38px]"
              title="Créer un nouveau fournisseur"
            >
              <Plus className="h-4 w-4 mr-1" />
              Nouveau
            </Button>
          </div>
          {orderErrors?.supplier && (
            <p className="mt-1 text-xs text-danger">
              {orderErrors.supplier.message}
            </p>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label
              htmlFor={`po-order-date-${index}`}
              className="block text-sm font-medium text-card-foreground mb-1"
            >
              Date d'achat
            </label>
            <input
              id={`po-order-date-${index}`}
              type="date"
              {...register(`orders.${index}.orderDate`)}
              className="w-full border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>

          <div>
            <label
              htmlFor={`po-delivery-date-${index}`}
              className="block text-sm font-medium text-card-foreground mb-1"
            >
              Livraison prévue
            </label>
            <input
              id={`po-delivery-date-${index}`}
              type="date"
              {...register(`orders.${index}.expectedDeliveryDate`)}
              className="w-full border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>

          <div>
            <label
              htmlFor={`po-notes-${index}`}
              className="block text-sm font-medium text-card-foreground mb-1"
            >
              Notes
            </label>
            <input
              id={`po-notes-${index}`}
              type="text"
              {...register(`orders.${index}.notes`)}
              className="w-full border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="Note facultative (lieu, paiement…)"
            />
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left py-2 px-3 text-xs font-medium text-muted-foreground">
                Ingrédient
                <LabelHint text="Si l'ingrédient a des conditionnements, choisissez celui dans lequel vous avez acheté (sac, carton…). Sinon, saisissez en unité de base." />
              </th>
              <th className="text-left py-2 px-3 text-xs font-medium text-muted-foreground w-32">
                Quantité
                <LabelHint text="Nombre d'unités de base ou de conditionnements selon votre choix. Ex: 2 sacs, ou 50 kg." />
              </th>
              <th className="text-left py-2 px-3 text-xs font-medium text-muted-foreground w-40">
                Prix unitaire
                <LabelHint text="Prix par unité de base, ou par conditionnement si sélectionné. Ex: 40 000 / kg ou 1 000 000 / sac." />
              </th>
              <th className="text-right py-2 px-3 text-xs font-medium text-muted-foreground w-32">
                Total
              </th>
              <th className="py-2 px-2 w-10" />
            </tr>
          </thead>
          <tbody>
            {fields.map((field, i) => {
              const line = watchedLines[i];
              const qty =
                parseFloat(
                  normalizeDecimalInput(line?.quantityOrdered ?? "") ?? ""
                ) || 0;
              const unitCents = parseMoneyToCents(line?.unitPrice) ?? 0;
              const total = qty * unitCents;
              const selectedIngredient = ingredientCache.current.get(
                line?.ingredient ?? ""
              );
              const packagings = selectedIngredient?.packagings ?? [];
              const selectedPackaging = packagings.find(
                (p) => p.id === line?.packaging
              );
              const baseUnit = selectedIngredient?.baseUnit ?? "";
              const packQty = selectedPackaging
                ? parseFloat(selectedPackaging.quantityInBase) || 0
                : 0;
              const baseEquivalentQty =
                selectedPackaging && qty > 0 ? qty * packQty : 0;
              const baseEquivalentPrice =
                selectedPackaging && unitCents > 0 && packQty > 0
                  ? unitCents / packQty
                  : 0;
              const lineErrors = orderErrors?.lines?.[i];
              return (
                <tr key={field.id} className="border-b border-border">
                  <td className="py-2 px-3 align-top">
                    <AsyncCombobox
                      value={line?.ingredient ?? ""}
                      onChange={(v) => {
                        setValue(`orders.${index}.lines.${i}.ingredient`, v, {
                          shouldValidate: true,
                        });
                        const next = ingredientCache.current.get(v);
                        const def = next?.packagings?.find((p) => p.isDefault);
                        setValue(
                          `orders.${index}.lines.${i}.packaging`,
                          def?.id ?? "",
                          { shouldValidate: false }
                        );
                      }}
                      options={ingredients.map((ing) => ({
                        value: ing.id,
                        label: ing.name,
                        sublabel: ing.baseUnit,
                      }))}
                      selectedLabel={
                        selectedIngredient
                          ? `${selectedIngredient.name} (${selectedIngredient.baseUnit})`
                          : undefined
                      }
                      placeholder="Ingrédient"
                      searchPlaceholder="Rechercher un ingrédient..."
                      emptyMessage="Aucun ingrédient"
                      isLoading={isFetchingIngredients}
                      onSearchChange={setIngredientSearch}
                    />
                    {lineErrors?.ingredient && (
                      <p className="mt-1 text-xs text-danger">
                        {lineErrors.ingredient.message}
                      </p>
                    )}
                    {packagings.length > 0 && (
                      <Select
                        value={line?.packaging || PACKAGING_BASE_SENTINEL}
                        onValueChange={(v) =>
                          setValue(
                            `orders.${index}.lines.${i}.packaging`,
                            v === PACKAGING_BASE_SENTINEL ? "" : v,
                            { shouldValidate: false }
                          )
                        }
                      >
                        <SelectTrigger className="mt-2 h-9 text-xs">
                          <SelectValue placeholder="Saisir en..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={PACKAGING_BASE_SENTINEL}>
                            Saisir en {baseUnit} (unité de base)
                          </SelectItem>
                          {packagings.map((p) => (
                            <SelectItem key={p.id} value={p.id}>
                              Saisir en {p.name} (1 {p.name} ={" "}
                              {parseFloat(p.quantityInBase)} {baseUnit})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </td>
                  <td className="py-2 px-3 align-top">
                    <input
                      type="text"
                      inputMode="decimal"
                      {...register(
                        `orders.${index}.lines.${i}.quantityOrdered`,
                        { setValueAs: stripSpaces }
                      )}
                      className="w-full border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      placeholder="0.00"
                    />
                    {selectedIngredient && (
                      <p className="text-xs text-muted-foreground mt-1">
                        {selectedPackaging ? selectedPackaging.name : baseUnit}
                      </p>
                    )}
                    {selectedPackaging && baseEquivalentQty > 0 && (
                      <p className="text-[11px] text-muted-foreground/80 mt-0.5">
                        = {baseEquivalentQty} {baseUnit}
                      </p>
                    )}
                    {lineErrors?.quantityOrdered && (
                      <p className="mt-1 text-xs text-danger">
                        {lineErrors.quantityOrdered.message}
                      </p>
                    )}
                  </td>
                  <td className="py-2 px-3 align-top">
                    <input
                      type="text"
                      inputMode="decimal"
                      {...register(`orders.${index}.lines.${i}.unitPrice`, {
                        setValueAs: stripSpaces,
                      })}
                      className="w-full border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      placeholder="0.00"
                    />
                    {selectedPackaging && (
                      <p className="text-xs text-muted-foreground mt-1">
                        / {selectedPackaging.name}
                      </p>
                    )}
                    {selectedPackaging && baseEquivalentPrice > 0 && (
                      <p className="text-[11px] text-muted-foreground/80 mt-0.5">
                        ≈ {displayPrice(baseEquivalentPrice, currency)} /{" "}
                        {baseUnit}
                      </p>
                    )}
                    {lineErrors?.unitPrice && (
                      <p className="mt-1 text-xs text-danger">
                        {lineErrors.unitPrice.message}
                      </p>
                    )}
                  </td>
                  <td className="py-2 px-3 align-top text-right text-sm font-medium">
                    {total > 0 ? displayPrice(total, currency) : "—"}
                  </td>
                  <td className="py-2 px-2 align-top">
                    <button
                      type="button"
                      onClick={() => remove(i)}
                      disabled={fields.length === 1}
                      className="h-8 w-8 rounded-md flex items-center justify-center text-muted-foreground hover:text-danger disabled:opacity-30 disabled:hover:text-muted-foreground"
                      aria-label="Supprimer"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={3} className="py-3 px-3 text-sm text-right font-medium">
                Total fournisseur
              </td>
              <td className="py-3 px-3 text-right text-base font-semibold text-card-foreground">
                {displayPrice(subtotal, currency)}
              </td>
              <td />
            </tr>
          </tfoot>
        </table>
      </div>

      <div>
        <ButtonAdd
          label="Ajouter une ligne"
          type="button"
          onClick={() => append(emptyOrderLine())}
        />
      </div>

      {typeof orderErrors?.lines?.message === "string" && (
        <p className="text-xs text-danger">{orderErrors.lines.message}</p>
      )}
    </div>
  );
};

export default BulkSupplierOrderBlock;
